import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function ThePlace() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>(".place-reveal").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 1.1,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 85%" },
          }
        );
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="place"
      ref={ref}
      className="relative bg-background-50 text-foreground-950 py-28 md:py-40 px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 place-reveal">
          <span className="w-8 h-px bg-foreground-950/40" />
          <span className="eyebrow text-foreground-700">01 — The Place</span>

        </div>

        <div className="grid md:grid-cols-12 gap-10 md:gap-16 mt-12">
          <h2 className="place-reveal md:col-span-7 font-display text-3xl md:text-[42px] leading-[1.08] tracking-tight text-foreground-950 font-light">
            A house that listens to the mountain.
          </h2>
          <div className="md:col-span-5 md:pt-4">
            <p className="place-reveal text-foreground-700 text-[15px] leading-relaxed font-light max-w-md">
              Set on a granite shelf above the tree line, Alpine Retreat is drawn from three honest materials — smoked larch, quarried stone, and glass tall enough to hold the weather. Inside, the light is slow. Outside, the world falls away.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-6 md:gap-8 mt-20">
          <div className="place-reveal md:col-span-7 h-[520px] md:h-[640px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Contemporary%20alpine%20architecture%20exterior%20in%20bright%20clear%20mountain%20daylight%2C%20smoked%20larch%20timber%20facade%20with%20tall%20glass%20windows%2C%20natural%20quarried%20stone%20base%2C%20nested%20into%20snow%20covered%20slope%20with%20pine%20trees%2C%20soft%20natural%20nordic%20daylight%2C%20editorial%20architectural%20photography%2C%20quiet%20luxury%2C%20mineral%20neutral%20palette%20of%20snow%20white%20mist%20grey%20timber%20brown%2C%20generous%20negative%20space&width=1600&height=1800&seq=place-arch-daylight-02&orientation=portrait&nocache=true"
              alt="Architecture"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="md:col-span-5 flex flex-col gap-6">
            <div className="place-reveal h-[280px] md:h-[310px] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Bright%20airy%20alpine%20lodge%20interior%20living%20room%20with%20natural%20oak%20floors%20and%20raw%20stone%20fireplace%2C%20low%20linen%20lounge%20sofa%2C%20wool%20throws%2C%20oversized%20panoramic%20window%20framing%20snowy%20mountain%20landscape%20in%20soft%20daylight%2C%20subtle%20warm%20interior%20fire%20light%2C%20editorial%20interior%20photography%2C%20quiet%20luxury%2C%20mineral%20neutral%20palette%20with%20timber%20warmth%2C%20negative%20space&width=1600&height=1200&seq=place-interior-editorial-02&orientation=landscape&nocache=true"
                alt="Interior"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="place-reveal grid grid-cols-1 gap-6 mt-2">
              {[
                { k: "Materials", v: "Smoked larch · quarried granite · float glass" },
                { k: "Architecture", v: "Designed with Studio Vals · completed 2024" },
                { k: "Setting", v: "48 hectares of protected alpine forest" },
              ].map((f) => (
                <div
                  key={f.k}
                  className="flex items-start justify-between gap-6 border-t border-foreground-950/15 pt-4"
                >
                  <span className="eyebrow text-foreground-700 shrink-0">{f.k}</span>
                  <span className="text-right text-foreground-900 font-display text-[15px] md:text-base leading-snug">
                    {f.v}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}